import { BENEFICIAIRE_CLES, CLE_REPARTITION_REVENUS, CORRESPONDANCE_TAUX_IP, ITT_UNITS, LOGGED_USER, SMIGS_AFRIQUE_OUEST } from "./constants";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeliveredProcedureOutlined,
    DollarCircleOutlined,
    FileDoneOutlined,
    FileProtectOutlined,
    HourglassOutlined,
    ShoppingCartOutlined,
    SyncOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import React from "react";
import { baremeCapitalisation } from "../pages/sinistres/components/utils/useHooks";

export const verifyNumber = (value) => {
    const numberRex = new RegExp("^[0-9]+$");
    return numberRex.test(value);
};

/**
 * Numéro à afficher sur les documents (condition particulière ET attestations) : le numéro
 * d'avenant s'il est significatif (≥ 4 caractères), sinon le numéro de police P, sinon M.
 * Un numéro d'avenant très court (< 4 car.) n'est pas un identifiant de police exploitable.
 */
export const numeroPoliceAffichage = (contrat) =>
    (contrat?.numeroAvenant && String(contrat.numeroAvenant).trim().length >= 4)
        ? contrat.numeroAvenant
        : (contrat?.numeroPoliceP || contrat?.numeroPoliceM || "");

// Déduplique une liste de paramètres (p_parametre) par libellé. Depuis le
// rattachement par catégorie (genre/carrosserie), une même valeur — ex. VOITURE —
// existe en plusieurs exemplaires (une ligne par catégorie) : la liste globale
// `/api/parametre/type/X` les remonterait toutes dans les Select.
export const dedupeParametresByLibelle = (list = []) => {
    const seen = new Set();
    return (Array.isArray(list) ? list : []).filter((p) => {
        const key = p?.libelleParametre;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

export const verifyDouble = (value) => {
    console.log("Data type: ", typeof value);
    const numberRex = new RegExp("^(?:\\d{0,5}\\.\\d{1,5})$|^\\d{0,5}$");
    return !numberRex.test(value);
};

export const customVerifyDouble = (value) => {
    if (isNaN(parseFloat(value))) {
        return false;
    }
    if (value.split(".")[1]?.length > 5 || parseFloat(value) > 100) {
        return false;
    }
    return true;
};

export const isToday = (dateString) => {
    const inputDate = new Date(dateString);
    const today = new Date();

    return (
        inputDate.getDate() === today.getDate() &&
        inputDate.getMonth() === today.getMonth() &&
        inputDate.getFullYear() === today.getFullYear()
    );
}

export const isEqualDate = (dateString, date) => {
    const inputDate = new Date(dateString);
    const day = new Date(date);

    return (
        inputDate.getDate() === day.getDate() &&
        inputDate.getMonth() === day.getMonth() &&
        inputDate.getFullYear() === day.getFullYear()
    );
}

export const isBetweenOrEqualDate = (date, startDate, endDate) => {
    const targetDate = new Date(date).getTime();
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();

    return targetDate >= start && targetDate <= end;
}

export const isTomonth = (dateString) => {
    const inputDate = new Date(dateString);
    const today = new Date();

    return (
        inputDate.getMonth() === today.getMonth() &&
        inputDate.getFullYear() === today.getFullYear()
    );
}

export const getAge = (birthdate) => {
    const today = new Date();
    const birth = new Date(birthdate);

    let age = today.getFullYear() - birth.getFullYear();

    // adjust if birthday hasn't occurred yet this year
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
    }

    return age;
}

export const getAgeFromSinistre = (birthdate, sinistreDate) => {
    const today = new Date(sinistreDate);
    const birth = new Date(birthdate);

    let age = today.getFullYear() - birth.getFullYear();

    // // adjust if birthday hasn't occurred yet this year
    // const m = today.getMonth() - birth.getMonth();
    // if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    //     age--;
    // }

    return age;
}

export const customVerifyPhoneNumber = (value) => {
    if (isNaN(parseFloat(value))) {
        return false;
    }
    return parseFloat(value) > 0;
};

export const customVerifyDoubleWithMin = (value, min) => {
    if (isNaN(parseFloat(value))) {
        return false;
    } else {
        if (value < min) {
            return false;
        } else {
            return true;
        }
    }
};

export const verifyLength = (value, length) => value?.length >= length;

export const verifyEmpty = (value, length) => value.length >= length;

export const verifyEmail = (value) => {
    const emailRex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRex.test(value);
};

export const dateView = (date) => {
    if (!date) return "";
    try {
        if (typeof date === "string" && date.includes("T")) {
            const [datePart, timePart] = date.split("T");
            return `${datePart} - ${timePart?.split(".")[0] || timePart}`;
        }
        return dayjs(date).format("YYYY-MM-DD - HH:mm:ss");
    } catch (e) {
        return String(date);
    }
};

export const frenchDate = (date) => {
    if (!date) return "";
    try {
        // If it's a string in ISO format
        if (typeof date === "string") {
            if (date.includes("T")) {
                const [datePart, timePart] = date.split("T");
                const [year, month, day] = datePart.split("-");
                return `${day}/${month}/${year} - ${timePart?.split(".")?.[0] || ""}`;
            }
            // fallback: try parseable string
            const d = dayjs(date);
            if (d.isValid()) return d.format("DD/MM/YYYY HH:mm:ss");
            return String(date);
        }
        // dayjs object / Date / number
        const d = dayjs(date);
        if (d.isValid()) return d.format("DD/MM/YYYY HH:mm:ss");
        return String(date);
    } catch (e) {
        return String(date);
    }

};

export const frenchDateOnly = (date) => {
    if (!date) return "";
    try {
        if (typeof date === "string" && date.includes("T")) {
            const [year, month, day] = date.split("T")[0].split("-");
            return `${day}/${month}/${year}`;
        }
        const d = dayjs(date);
        if (d.isValid()) return d.format("DD/MM/YYYY");
        return String(date);
    } catch (e) {
        return String(date);
    }

};

// Justificatif de paiement d'un sinistre (bordereau des sinistres payés) : bénéficiaire, n° de
// chèque, banque et DATE D'ÉMISSION DU CHÈQUE. Construit à partir des champs du décaissement
// (DecaissementSearchDTO / lignes de bordereau) — pas de champ « justificatif » dédié. Côté
// backend, le champ `dateReglement` des feeds sinistres payés porte COALESCE(date_emission_cheque,
// date_reglement) : pour une SORTIE c'est la date d'émission du chèque, pour un recours (sans date
// d'émission) c'est la date de règlement. On accepte aussi un champ dédié dateEmissionCheque s'il
// existe (prioritaire), sinon dateReglement, sinon dateCheque.
export const formatJustificatifCheque = (it) => {
    if (!it) return "";
    const parts = [];
    if (it.beneficiaire) parts.push(String(it.beneficiaire).trim());
    const cheque = [];
    if (it.numeroCheque) cheque.push(`Chèque N° ${it.numeroCheque}`);
    if (it.banque) cheque.push(String(it.banque).trim());
    if (cheque.length) parts.push(cheque.join(" - "));
    const d = it.dateEmissionCheque || it.dateReglement || it.dateCheque;
    if (d) parts.push(frenchDateOnly(d));
    return parts.join(" · ");
};

// Date de souscription = date de validation de la proposition en contrat.
// Regle metier : si la date de validation existe, c'est la date de souscription.
// Sinon (anciens contrats sans date de validation), on retombe sur updatedAt
// afin de toujours afficher une valeur coherente. La date de saisie (createdAt)
// n'est volontairement pas utilisee.
export const getDateSouscription = (contrat) => {
    if (!contrat) {
        return null;
    }
    // 1) Date de validation (proposition -> contrat) : nom de champ tolerant
    //    (camelCase ou snake_case selon la serialisation backend).
    // 2) Repli anciens contrats : updatedAt / updated_at.
    return (
        contrat.dateValidation ||
        contrat.dateSouscription ||
        contrat.validatedAt ||
        contrat.date_validation ||
        contrat.updatedAt ||
        contrat.updated_at ||
        null
    );
};

export const frenchDateSimple = (date) => {
    const [datePart, timePart] = date.split("T");
    const [year, month, day] = datePart.split("-");
    return `${day}/${month}/${year} - ${timePart || ""}`;
};

export const displayDateEcheance = (date) => {
    if (date) {
        return dayjs(date)
            .hour(23)
            .minute(59)
            .second(59)
            .format("DD/MM/YYYY HH:mm:ss");
    } else {
        return "";
    }
}

export const frenchMonth = (month) => {
    const months = {
        "01": "Janvier", "02": "Février", "03": "Mars", "04": "Avril",
        "05": "Mai", "06": "Juin", "07": "Juillet", "08": "Août",
        "09": "Septembre", "10": "Octobre", "11": "Novembre", "12": "Décembre"
    };
    return months[month] || "";
};

const ones = [
    "", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf",
    "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize",
    "dix-sept", "dix-huit", "dix-neuf"
];

const tens = [
    "", "", "vingt", "trente", "quarante", "cinquante", "soixante"
];

function getBelowHundred(n) {
    if (n < 20) return ones[n];

    if (n < 70) {
        const ten = Math.floor(n / 10);
        const unit = n % 10;
        if (unit === 1 && (ten === 2 || ten === 3 || ten === 4 || ten === 5 || ten === 6)) {
            return `${tens[ten]} et un`;
        } else {
            return `${tens[ten]}${unit ? "-" + ones[unit] : ""}`;
        }
    }

    if (n < 80) {
        const base = "soixante";
        return `${base}-${getBelowHundred(n - 60)}`;
    }

    if (n < 100) {
        const base = "quatre-vingt";
        if (n === 80) return base + "s";
        return `${base}-${getBelowHundred(n - 80)}`;
    }

    return "";
}

function getBelowThousand(n) {
    if (n < 100) return getBelowHundred(n);

    const hundred = Math.floor(n / 100);
    const rest = n % 100;

    let prefix = hundred === 1 ? "cent" : ones[hundred] + " cent";

    // "cents" takes 's' only if no remainder
    if (rest === 0 && hundred > 1) {
        prefix += "s";
    }

    return rest ? `${prefix} ${getBelowHundred(rest)}` : prefix;
}

export const numberToWordsFrench = (num) => {
    if (num === 0) return "ZÉRO";

    if (num < 0) return "MOINS " + numberToWordsFrench(-num);

    const parts = [];

    const milliards = Math.floor(num / 1_000_000_000);
    if (milliards) {
        parts.push(`${numberToWordsFrench(milliards)} milliard${milliards > 1 ? "s" : ""}`);
        num %= 1_000_000_000;
    }

    const millions = Math.floor(num / 1_000_000);
    if (millions) {
        parts.push(`${numberToWordsFrench(millions)} million${millions > 1 ? "s" : ""}`);
        num %= 1_000_000;
    }

    const milliers = Math.floor(num / 1000);
    if (milliers) {
        if (milliers === 1) {
            parts.push("mille");
        } else {
            parts.push(`${numberToWordsFrench(milliers)} mille`);
        }
        num %= 1000;
    }

    if (num > 0) {
        parts.push(getBelowThousand(num));
    }

    return parts.join(" ").trim().toUpperCase();
};


export const getvalue = (event) => {
    return event.target.value;
};

export const getValue = (event) => {
    return event;
};

export const getIdentificateur = (event) => {
    if (event.target.id) {
        return event.target.id;
    }
    if (event.target.name) {
        return event.target.name;
    }
};

export const getidentificateur = (value, name) => {
    return name;
};

export const setValue = (
    valeur,
    identificateur,
    stateSetter,
    errorStateSetter,
    typeOfControl
) => {
    stateSetter((lastState) => {
        return {
            ...lastState,
            [identificateur]: valeur,
        };
    });
    errorStateChecker(identificateur, valeur, errorStateSetter, typeOfControl);
};

export const checkString = (input) => {
    if (input === null) {
        return true;
    }
    if (input?.trim().length < 1 || !isNaN(input)) {
        return true;
    }
    return false;
};

export const checkDate = (input) => {
    if (input === null) {
        return true;
    }
    if (input?.trim().length < 1 || !isNaN(input)) {
        return true;
    }
    const date = new Date(input);
    const now = new Date();
    if (date <= now) {
        return true;
    }

    return false;
};

export const checkNumber = (input) => {
    if (input === null) {
        return true;
    }
    if (input.length < 1 || isNaN(input)) {
        return true;
    }
    return false;
};

export const errorStateChecker = (
    identificateur,
    valeur,
    errorStateSetter,
    typeOfControl
) => {
    if (typeOfControl === "string") {
        errorStateSetter((lastState) => {
            // console.log({ ...lastState, [identificateur]: checkString(valeur) });
            return { ...lastState, [identificateur]: checkString(valeur) };
        });
    }
    if (typeOfControl === "alphanumeric") {
        const isInvalid = (() => {
            if (valeur === null || valeur === undefined) return true;
            const str = String(valeur).trim();
            if (str.length < 1) return true;
            // Only letters and digits allowed
            return !/^[a-zA-Z0-9]+$/.test(str);
        })();
        errorStateSetter((lastState) => {
            return { ...lastState, [identificateur]: isInvalid };
        });
    }
    if (typeOfControl === "number") {
        errorStateSetter((lastState) => {
            // console.log({ ...lastState, [identificateur]: checkNumber(valeur) });
            return { ...lastState, [identificateur]: checkNumber(valeur) };
        });
    }
    if (typeOfControl === "date") {
        errorStateSetter((lastState) => {
            return { ...lastState, [identificateur]: checkString(valeur) };
        });
    }
};

export const toutesLesValeursSontValides = (objet) => {
    for (let key in objet) {
        if (objet[key] !== false) {
            return false;
        }
    }
    return true;
};

export const formaterLesClients = (listeDesClients) => {
    return listeDesClients
        .map((client, index) => {
            if (client.numeroAssureM !== null && client.nom !== null) {
                const label =
                    client.prenom !== null
                        ? `${client.nom} ${client.prenom}-${client.numeroAssureM}-${index}`
                        : `${client.nom}-${client.numeroAssureM}-${index}`;
                return {
                    value: client.personneId,
                    label: label.trim(),
                    numeroAssure: client.numeroAssureM,
                };
            }
            return null;
        })
        .filter((value) => value !== null);
};

export const formaterLesContrats = (listeDesContrats) => {
    return listeDesContrats
        .map((contrat) => {
            if (contrat.numeroPoliceM !== null) {
                const label =
                    contrat?.prenom !== null
                        ? `${contrat.numeroPoliceM} - ${frenchDateOnly(contrat?.dateEffet)} - ${contrat?.nom} ${contrat?.prenom} - ${contrat?.immatriculation}`
                        : `${contrat.numeroPoliceM} - ${frenchDateOnly(contrat?.dateEffet)} - ${contrat?.nom} - ${contrat?.immatriculation}`;
                return {
                    value: contrat.codeId,
                    label: label.trim(),
                    numeroPolice: contrat.codeId,
                    contrat: contrat,
                };
            } else if (
                contrat.numeroPoliceP !== null &&
                contrat.nom !== null
            ) {
                const label =
                    contrat?.prenom !== null
                        ? `${contrat.numeroPoliceP} - ${frenchDateOnly(contrat?.dateEffet)} - ${contrat?.nom} ${contrat?.prenom} - ${contrat?.immatriculation}`
                        : `${contrat.numeroPoliceP} - ${frenchDateOnly(contrat?.dateEffet)} - ${contrat?.nom} - ${contrat?.immatriculation}`;
                return {
                    value: contrat.codeId,
                    label: label.trim(),
                    numeroPolice: contrat.codeId,
                    contrat: contrat,
                };
            }
            return null;
        })
        .filter((value) => value !== null);
};

// Get engins from contrat
export const getEnginsFromContrat = (contrat) => {
    return [{
        value: contrat?.immatriculation,
        label: contrat?.immatriculation,
    }];
};

// Formater les entites

export const formaterLesEntites = (listeDesEntites) => {
    return listeDesEntites.map((entite) => {
        return {
            value: entite.entiteConstatId,
            label: entite.adresse !== null ? `${entite?.nom} - ${entite?.adresse}` : `${entite?.nom}`,
            entite: entite,
        };
    });
};

export const convertDateFormatDDMMYYYYToYYYYMMDD = (date) => {
    // Validate the input format using a regular expression
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = date.match(regex);

    if (!match) {
        throw new Error("Invalid date format. Expected DD/MM/YYYY.");
    }

    // Extract day, month, and year
    const day = match[1];
    const month = match[2];
    const year = match[3];

    // Return the reformatted date
    return `${year}-${month}-${day}`;
}

export const formatFrenchDate = (dateInput) => {
    const date = new Date(dateInput);
    const options = {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    };

    // Format the date in French
    return date.toLocaleDateString("fr-FR", options).replace(",", " à");
}

export const frenchDatews = (date) => {
    if (date) {
        const [datePart, timePart] = date?.split("T");
        const [year, month, day] = datePart?.split("-");
        const [hour, minute] = timePart?.split(":");
        return `${day}/${month}/${year} à ${hour}:${minute}`;
    } else {
        return "";
    }
};

export const getInitiator = (actions) => {
    return actions?.filter((action) => action.action === 'INITIER' || action.action === 'VALIDER')[0]?.acteur
}

export const isConnectedUser = (actions) => {
    return LOGGED_USER?.email === getInitiator(actions)?.email
}

export const isConnectedUserFromPOOL = () => {
    return LOGGED_USER?.profil?.organisme?.typeOrganisme === 'POOL'
}

export const getCodeHabilitation = (statutCommande) => {
    if (statutCommande === "INITIE") {
        return "HCO071";
    } else if (statutCommande === "VALIDE") {
        return "HCO072";
    } else if (statutCommande === "AUTORISE") {
        return "HCO073";
    } else if (statutCommande === "EN_TRAITEMENT") {
        return "HCO074";
    } else if (statutCommande === "APPROUVE") {
        return "HCO075";
    } else if (statutCommande === "REGLE") {
        return "HCO076";
    }
};

export const getActionVerb = (statutCommande) => {
    switch (statutCommande) {
        case "INITIE":
            return "valider";
        case "VALIDE":
            return "autoriser";
        case "AUTORISE":
            return "receptionner et mettre en traitement";
        case "EN_TRAITEMENT":
            return "approuver";
        case "APPROUVE":
            return "régler";
        case "REGLE":
            return "livrer";
        case "DELIVRE":
            return "réceptionner";
        default:
            return null;
    }
};

export const getAntdIcon = (status) => {
    const statusIconMap = {
        INITIE: <ShoppingCartOutlined />, // Order placed
        VALIDE: <CheckCircleOutlined />, // Order validated
        AUTORISE: <SyncOutlined />, // Order authorized
        EN_TRAITEMENT: <HourglassOutlined />, // Order in processing
        APPROUVE: <FileProtectOutlined />, // Order approved
        REGLE: <DollarCircleOutlined />, // Payment completed
        DELIVRE: <DeliveredProcedureOutlined />, // Order delivered
        RECEPTIONNE: <FileDoneOutlined />, // Order received
        REJETE: <CloseCircleOutlined />, // Order rejected
    }
    return statusIconMap[status] || null;
};

/**
 * Formats a number as currency.
 *
 * @param {number} amount - The number to format.
 * @param {string} locale - The locale identifier (e.g., "en-US", "fr-FR").
 * @param {string} currency - The ISO currency code (e.g., "USD", "EUR").
 * @returns {string} - The formatted currency string.
 */
export const formatCurrencyWithCurrencyCode = (amount, locale = 'fr-FR', currency = 'XOF') => {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
    }).format(amount);
}

export const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount);
}

export const getAction = (actions, actionTypes) => {
    return actions?.filter(action => actionTypes.includes(action.action)) || [];
}

export const getDurationBetweenDates = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start) || isNaN(end)) {
        return "Date(s) invalide(s)";
    }

    let diffMs = Math.abs(end - start); // difference in milliseconds

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    diffMs %= (1000 * 60 * 60 * 24);

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    diffMs %= (1000 * 60 * 60);

    const minutes = Math.floor(diffMs / (1000 * 60));
    diffMs %= (1000 * 60);

    const seconds = Math.floor(diffMs / 1000);

    return `${days}j ${hours}h ${minutes}m ${seconds}s`;
}

export const getDaysDurationBetweenDates = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start) || isNaN(end)) {
        return "Date(s) invalide(s)";
    }

    let diffMs = Math.abs(end - start); // difference in milliseconds

    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

export const numberOfDaysBetweenDates = (startDate, endDate) => {
    console.log(startDate, endDate);
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDiff = Math.abs(end - start);
    console.log(timeDiff);
    const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return diffDays;
}


export const transformImmatriculation = (input) => {
    if (input === null) {
        return "N/A";
    }
    // Vérifie si l'entrée respecte déjà le format attendu
    const correctFormatRegex = /^[A-Z]{2}\sTG\s\d+$/;
    if (correctFormatRegex.test(input)) {
        return input; // Retourne l'entrée si elle est déjà correcte
    }

    // Vérifie si l'entrée est au format initial à transformer
    const initialFormatRegex = /^([A-Z]{2})\s(\d+)\s(TG)$/;
    const match = input.match(initialFormatRegex);

    if (!match) {
        return input; // Retourne l'entrée si elle ne contient pas TG ou n'est pas au bon format
    }

    // Décompose l'immatriculation en parties
    const letters = match[1]; // Les deux premières lettres
    const numbers = match[2]; // La séquence numérique
    const country = match[3]; // Le code du pays (toujours TG)

    // Reconstruit le format attendu
    return `${letters} ${country} ${numbers}`;
};

export const splitNameStringForPrintingAttestation = (input, maxChars) => {
    // Ensure the input is a string and the maxChars is a positive number
    if (
        typeof input !== "string" ||
        typeof maxChars !== "number" ||
        maxChars <= 0
    ) {
        throw new Error("Invalid input or maximum character limit");
    }

    let firstLine = "";
    let secondLine = "";

    // Split the string into words
    const words = input.split(" ");

    for (let word of words) {
        // Check if adding the next word exceeds the maxChars limit
        if ((firstLine + word).length <= maxChars) {
            firstLine += (firstLine ? " " : "") + word; // Add space only if it's not the first word
        } else {
            // The remaining part goes to the second line
            secondLine += (secondLine ? " " : "") + word;
        }
    }

    return { firstLine, secondLine };
};

export const getAgeCategory = (age) => {
    console.log("Calculating age category for age:", age);
    if (age < 15) return "Moins_de_15_ans";
    if (age >= 15 && age <= 19) return "De_15_a_19_ans";
    if (age >= 20 && age <= 24) return "De_20_a_24_ans";
    if (age >= 25 && age <= 29) return "De_25_a_29_ans";
    if (age >= 30 && age <= 39) return "De_30_a_39_ans";
    if (age >= 40 && age <= 59) return "De_40_a_59_ans";
    if (age >= 60 && age <= 69) return "De_60_a_69_ans";
    if (age >= 70) return "Soixante_dix_ans_et_plus";
    return null; // Retourne null si l'âge n'est pas dans une catégorie
};

export const getValeurPointIP = (tauxIP, age) => {
    console.log("Calculating valeur point IP for tauxIP:", tauxIP, "and age:", age);
    if (typeof tauxIP !== 'number' || typeof age !== 'number') {
        console.error("Les paramètres tauxIP et age doivent être des nombres.");
        return null;
    }

    const ageCategory = getAgeCategory(age);
    if (!ageCategory) {
        console.error("Tranche d'âge non trouvée pour l'âge donné.");
        return null;
    }

    const tauxIPRow = CORRESPONDANCE_TAUX_IP.find(
        (row) => tauxIP >= row.tauxIP.min && tauxIP <= row.tauxIP.max
    );

    if (!tauxIPRow) {
        console.error("Aucune correspondance de taux d'IP trouvée.");
        return null;
    }

    // Accède à la valeur du point
    return tauxIPRow.age[ageCategory];
};

/**
 * Accède à la valeur du prix de la rente en fonction de l'âge et du sexe.
 * @param {number} age - L'âge de la personne (entre 0 et 100).
 * @param {string} sexe - Le sexe de la personne ('MASCULIN' ou 'FEMININ').
 * @returns {number|null} Le prix de la rente ou null si aucune correspondance n'est trouvée.
 */
export const getPrixRente = async (age, sexe, baremeCapitalisationToChoose) => {
    console.log("Calculating prix rente for age:", age, "and sexe:", sexe, " baremeCapitalisationToChoose: ", baremeCapitalisationToChoose);
    if (typeof age !== 'number' || age < 0 || age > 100) {
        console.error("L'âge doit être un nombre entre 0 et 100.");
        return null;
    }

    if (sexe !== 'MASCULIN' && sexe !== 'FEMININ') {
        console.error("Le sexe doit être 'MASCULIN' ou 'FEMININ'.");
        return null;
    }
    console.log("Fetching baremeCapitalisation for age:", age, "and sexe:", sexe, " bareme: ", baremeCapitalisationToChoose);
    let genre = 'M';
    if (sexe !== 'MASCULIN') {
        genre = 'F';
    }

    // Sélectionne le barème approprié en fonction du sexe
    // const bareme = BAREME_CAPITALISATION[sexe];

    const bareme = await baremeCapitalisation(baremeCapitalisationToChoose, age, genre);

    console.log("Bareme fetched:", bareme);
    // Cherche l'objet correspondant à l'âge
    // const rente = bareme.find(item => item.age === age);
    const rente = bareme.data;

    if (rente) {
        return rente.prixUnFrancRente;
    } else {
        console.warn(`Aucun prix de rente trouvé pour l'âge ${age} et le sexe ${sexe}.`);
        return null;
    }
};


/**
 * Récupère le SMIG d'un pays pour une année donnée en utilisant une structure optimisée.
 * @param {string} countryName - Le nom du pays (ex: "Togo", "Sénégal").
 * @param {number} year - L'année souhaitée (ex: 2023).
 * @returns {number|null} Le montant du SMIG ou null si le pays ou l'année n'est pas trouvée.
 */
export const getSmigByCountryAndYear = (countryName, year) => {
    const countryData = SMIGS_AFRIQUE_OUEST[countryName];

    if (!countryData) {
        console.error(`Le pays "${countryName}" n'est pas dans la base de données.`);
        return null;
    }

    // Trier par année pour s'assurer que nous trouvons la dernière valeur en ordre décroissant
    const sortedData = [...countryData].sort((a, b) => b.anneeDebut - a.anneeDebut);

    // Chercher la dernière entrée dont l'année de début est <= à l'année recherchée
    const smigEntry = sortedData.find(entry => year >= entry.anneeDebut);

    if (!smigEntry) {
        console.warn(`Aucune donnée de SMIG trouvée pour l'année ${year} en ${countryName}.`);
        return null;
    }

    return smigEntry.smig;
};


/**
 * Récupère le pourcentage de répartition des revenus en fonction de la situation familiale et de l'ayant droit.
 * @param {string} situation - La situation familiale (ex: "CONJOINT AVEC JUSQU'À 4 ENFANTS À CHARGE").
 * @param {string} ayantDroit - Le type d'ayant droit. Les valeurs acceptées sont : "ascendant", "conjoint", "enfant", "orphelinDouble".
 * @returns {number|null} Le pourcentage de répartition ou null si la situation ou le type d'ayant droit n'est pas trouvé.
 */
export const getPourcentageRepartition = (situation, ayantDroit) => {
    // Vérification des paramètres
    if (!situation || !ayantDroit) {
        console.error("Veuillez fournir la situation et le type d'ayant droit.");
        return null;
    }

    // Cherche la ligne correspondant à la situation familiale
    const situationRow = CLE_REPARTITION_REVENUS.find(row => row.situation === situation);

    if (!situationRow) {
        console.error(`Situation familiale non trouvée : "${situation}"`);
        return null;
    }

    // Accède au pourcentage en fonction du type d'ayant droit
    const pourcentage = situationRow[ayantDroit];

    if (pourcentage === undefined) {
        console.error(`Type d'ayant droit non valide pour cette situation : "${ayantDroit}"`);
        return null;
    }

    return pourcentage;
};

/**
 * Récupère la clé du prejudice moral associée à un ayant droit.
 * @param {string} ayantDroit - Le nom du bénéficiaire (ex: "PERE", "CONJOINT(E)").
 * @returns {number|null} La clé du bénéficiaire ou null si le bénéficiaire n'est pas trouvé.
 */
export const getPrejudiceMoralBeneficiaireKey = (ayantDroit) => {
    if (!ayantDroit || typeof ayantDroit !== 'string') {
        console.error("Veuillez fournir un nom de bénéficiaire valide.");
        return 0;
    }

    // Utilise toUpperCase() pour gérer les entrées en minuscules ou majuscules
    const key = BENEFICIAIRE_CLES[ayantDroit.toUpperCase()];

    if (key === undefined) {
        console.warn(`Aucune clé trouvée pour le bénéficiaire : "${ayantDroit}".`);
        return 0;
    }

    return key;
};

/**
 * Vérifie si l'année de la date passée en paramètre est la même que l'année courante.
 * @param {string} dateStr - La date à vérifier au format "AAAA-MM-JJ".
 * @returns {boolean} true si c'est la même année, false sinon.
 */
export const isSameYear = (dateStr) => {
    const givenDate = new Date(dateStr);
    const currentYear = new Date().getFullYear();
    return givenDate.getFullYear() === currentYear;
}

export const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
}

export const parseImmatriculation = (immatriculation) => {
    if (!immatriculation || typeof immatriculation !== 'string') {
        return { typeSerie: '', numeroSerie: '', matricule: '' };
    }

    // Normaliser : enlever espaces et mettre en majuscules
    const cleaned = immatriculation.toUpperCase().replace(/\s+/g, '');

    // Extraire toutes les parties : lettres et chiffres
    const parts = cleaned.match(/[A-Z]+|\d+/g);
    if (!parts || parts.length < 2) {
        return { typeSerie: '', numeroSerie: '', matricule: '' };
    }

    // Identifier la partie numérique (matricule)
    const numericPart = parts.find(part => /^\d+$/.test(part));
    const letters = parts.filter(part => /^[A-Z]+$/.test(part));

    if (!numericPart || letters.length < 2) {
        // Si on n’a pas assez de parties, tentative basique
        const match = cleaned.match(/^([A-Z]{2})(TG)(\d{4})$|^([A-Z]{2})(\d{4})(TG)$|^(TG)(\d{4})([A-Z]{2})$/);
        if (match) {
            const [, ns1, ts1, m1, ns2, m2, ts2, ts3, m3, ns3] = match;
            return {
                typeSerie: ts1 || ts2 || ts3 || 'TG', // suppose TG si pas trouvé
                numeroSerie: ns1 || ns2 || ns3 || '',
                matricule: m1 || m2 || m3 || '',
            };
        }
        return { typeSerie: '', numeroSerie: '', matricule: '' };
    }

    // On suppose que "TG" est le typeSerie
    // const typeSerie = letters.includes('TG') ? 'TG' : letters.find(l => l === 'TG') || 'TG';

    // Les deux autres blocs de lettres : l’un est numeroSerie, l’autre est typeSerie
    const otherLetters = letters.filter(l => l !== 'TG');
    const numeroSerie = otherLetters.length > 0 ? otherLetters[0] : '';

    return {
        typeSerie: 'TG', // fixe pour ton cas, ou utiliser `typeSerie` si variable
        numeroSerie,
        matricule: numericPart,
    };
}

export const formatImmatriculationForProduction = (immatriculation) => {
    if (!immatriculation || typeof immatriculation !== 'string') {
        return '';
    }

    const { typeSerie, numeroSerie, matricule } = parseImmatriculation(immatriculation);
    const segments = [];

    if (typeSerie) {
        segments.push(typeSerie.toUpperCase());
    }

    if (matricule) {
        segments.push(matricule.toUpperCase());
    }

    if (numeroSerie) {
        segments.push(numeroSerie.toUpperCase());
    }

    const formatted = segments.join(' ').trim();

    if (formatted) {
        return formatted;
    }

    return immatriculation.toUpperCase().replace(/\s+/g, ' ').trim();
}

export const composeImmatriculation = (typeSerie, matricule, numeroSerie) => {
    const parts = [typeSerie, matricule, numeroSerie]
        .map((part) => (typeof part === 'string' ? part.trim().toUpperCase() : ''))
        .filter(Boolean);

    if (parts.length === 0) {
        return '';
    }

    return parts.join(' ').replace(/\s+/g, ' ').trim();
}

const trimTrailingZeros = (value) => {
    const numericString = `${value}`;
    if (!numericString.includes('.')) {
        return numericString;
    }
    return numericString.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
};

export const getIttUnitLabel = (unit = 'JOURS') => {
    const option = ITT_UNITS.find((item) => item.value === unit);
    return option?.label || 'Jours';
};

export const convertIttValueToDays = (value, unit = 'JOURS') => {
    const numericValue = parseFloat(value);
    if (Number.isNaN(numericValue)) {
        return 0;
    }

    switch (unit) {
        case 'SEMAINES':
            return numericValue * 7;
        case 'MOIS':
            return numericValue * 30;
        default:
            return numericValue;
    }
};

export const convertDaysToIttValue = (days, unit = 'JOURS') => {
    const numericValue = parseFloat(days);
    if (Number.isNaN(numericValue)) {
        return 0;
    }

    switch (unit) {
        case 'SEMAINES':
            return numericValue / 7;
        case 'MOIS':
            return numericValue / 30;
        default:
            return numericValue;
    }
};

export const formatIttValueForDisplay = (days, unit = 'JOURS') => {
    const value = convertDaysToIttValue(days, unit);
    if (!Number.isFinite(value)) {
        return '0';
    }

    const rounded = Math.round(value * 100) / 100;
    const sanitized = rounded === 0 ? 0 : rounded;
    return trimTrailingZeros(sanitized);
};

/**
 * Résout le « Site » à afficher sur les documents (condition particulière / proposition).
 * Priorité : agence courtier → agence du producteur → siège de l'organisme.
 * @param {object} contrat
 * @returns {string}
 */
export const resolveSiteProduction = (contrat) => {
    if (contrat?.courtier?.nom) {
        return contrat.courtier.nom;
    }
    if (contrat?.producteur?.agence?.nom) {
        return contrat.producteur.agence.nom;
    }
    return `Siège ${contrat?.organisme?.raisonSociale ?? ""}`.trim();
};
